import { Button, Grid, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { SiTrackPage } from '../../components/si-track-page';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { projectsApiKeyStore } from '../../api/projects-api-key-store';
import { columns } from './projects-grid-columns';
import { ProjectCustomGridCell } from './project-custom-grid-cell';
import { Project } from '../../models/project';
import { useEffect, useState } from 'react';
import { SelectChainItem, SelectResult } from '../../components/select/select-chain.component';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { GuardedComponent } from '../../components/guarded-component';
import { UserPermissionName } from '../../models/user-permission-name';
import { FormSelect } from '../../components/hook-form/form-select.component';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { useLoadData } from '../../hooks/useLoadData';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { ApiResponse } from '../../models/api-response';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { useSaveData } from '../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    searchText: params.get('searchText'),
    status: 'active',
  };
};

const options = [
  {
    value: 'all',
    label: 'All',
    isDefault: false,
  },
  {
    value: 'active',
    label: 'Active',
    isDefault: true,
  },
  {
    value: 'archived',
    label: 'Archived',
    isDefault: false,
  },
];

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedFilter, setSelectedFilter] = useState<SelectResult>(getFiltesFromUrl(searchParams));
  const form = useReactHookForm({
    defaultValues: {
      status: 'active',
    },
  });

  const handleCreateProject = () => {
    navigate('/projects/create');
  };

  const handleEditProject = (value: Project) => {
    navigate(`edit/${value.projectID}`);
  };

  const handleFilterSelected = (filter: SelectResult) => {
    setSelectedFilter(filter);

    if (filter) {
      setSearchParams({
        ...(filter.customerID && { customerID: filter.customerID }),
        ...(filter.officeID && { officeID: filter.officeID }),
      });
    }
  };

  const getAPIKeyStore = () => {
    const { customerID, officeID, searchText } = selectedFilter;

    if (officeID) {
      // return projectsApiKeyStore.getOfficeProjects(officeID);
      return projectsApiKeyStore.getOfficeProjectswithStatus(officeID, form.getValues('status'));
    }
    if (customerID) {
      // return projectsApiKeyStore.getCustomerProjects(customerID);
      return projectsApiKeyStore.getCustomerProjectsWithStatus(customerID, form.getValues('status'));
    }
    if (searchParams.get('searchText') as string) {
      return projectsApiKeyStore.getSearchProjects(searchParams.get('searchText'));
    }

    // return projectsApiKeyStore.getProjects();
    return projectsApiKeyStore.getProjectsWithStatus(form.getValues('status'));
  };

  const handleStatusChange = (e: any) => {
    setSelectedFilter({ ...selectedFilter, status: e });
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Project', 'Project'));

  const { isLoading, data: projects, refetch } = useLoadData<ApiResponse<Project[]>>(getAPIKeyStore());

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredProjectList = projects?.data?.filter((Project) =>
      Object.values(Project).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredProjects(filteredProjectList || []);
  }, [debouncedSearchTerm, projects?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Project',
      pageName: 'Project',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Project',
      pageName: 'Project',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------


  const isUATEnable = process.env.REACT_APP_UAT_FLAG === 'true';
  return (
    <SiTrackPage
      title="Projects"
      actionButton={
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_PROJECT]}>
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={handleCreateProject}
          >
            Add Project
          </Button>
        </GuardedComponent>
      }
    >
      <div className="innerContent">
        <ReactHookForm formName="SEVE_FORM" onSubmit={() => { }} formContextProps={form}>
          <div className="mBm d-flex">
            <SelectChainItem
              config={[
                {
                  title: 'Customer',
                  optionsLabelKey: 'customerName',
                  optionsValueKey: 'customerID',
                  type: 'autocomplete',
                  getQueryKeyConfig: () => customersApiKeyStore.getCustomers(),
                },
                {
                  title: 'Office',
                  optionsLabelKey: 'officeName',
                  optionsValueKey: 'officeID',
                  type: 'autocomplete',
                  getQueryKeyConfig: (value?: SelectResult) => officeApiKeyStore.getCustomerOffices(value?.customerID),
                },
              ]}
              value={selectedFilter}
              onSelect={handleFilterSelected}
            />

            <FormSelect label="Status" name="status" options={options} onChange={handleStatusChange} />
          </div>
        </ReactHookForm>
        <Paper elevation={0} style={styling.paperStyle}>
          <SiTrackGrid
            fileTitle='project'
            columns={columns.map((col) =>
              col.field === 'forecastStartDate' || col.field === 'estimatedStartsPerMonth'
                ? { ...col, hide: !isUATEnable }
                : col
            )}
            dataItemKey="projectID"
            customCell={(field) =>
              ProjectCustomGridCell({
                field,
              })
            }
            // queryConfig={getAPIKeyStore()}
            viewRoute={(value) => `details/${value.projectID}`}
            // onEdit={handleEditProject}
            onEdit={form.getValues('status') !== 'archived' ? handleEditProject : undefined}
            actionPermissions={{ edit: UserPermissionName.EDIT_PROJECT, export: UserPermissionName.DOWNLOAD_GRID }}
            isBusy={isLoading}
            // -----------------Grid Layout-------
            dataItems={filteredProjects || []}
            reorderable
            handleSearch={handleInputChange}
            handleSaveLayout={handleSaveLayout}
            isSaveLayout={true}
            gridConfigFromApi={gridConfig}
            handleResetLayout={handleResetLayout}
            reFixPageNumber
          />
        </Paper>
      </div>
    </SiTrackPage>
  );
};
